import { useCallback, useRef, useState } from 'react';
import { Button, IconHighlight, ModalLayout } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import { CENTERED_PANEL, WIDE_MODAL } from '../shared/modalClasses';

const TITLE_ID = 'confirm-action-title';
/**
 * Confirm dialog shared by suspend, change of user type and revoke invitation. `onConfirm`
 * resolves `true` when the action worked (the dialog closes) and `false` when it failed (the
 * dialog stays open so the user can retry or cancel).
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {() => Promise<boolean>} props.onConfirm
 * @param {string} props.title
 * @param {string} props.message
 * @param {string} props.confirmLabel
 * @param {'primary'|'danger'} [props.confirmVariant]
 * @param {import('lucide-react').LucideIcon} [props.icon] Highlighted icon before the title.
 * @param {string} [props.iconTone='Error'] `IconHighlight` color of that icon (`Error`, `Alert`…).
 */
function ConfirmActionModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  confirmVariant = 'primary',
  icon,
  iconTone = 'Error',
}) {
  const { t } = useTranslation('people-access');
  const [submitting, setSubmitting] = useState(false);
  // The ref blocks a double click before React re-renders with `submitting`.
  const submittingRef = useRef(false);

  const handleConfirm = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    let ok = false;
    try {
      ok = await onConfirm();
    } catch {
      // `onConfirm` is meant to resolve false on failure; a throw is treated the same way.
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
    if (ok) onClose();
  }, [onConfirm, onClose]);

  // Esc closes unless a request is in flight.
  const handleEscape = useCallback(() => {
    if (!submittingRef.current) onClose();
  }, [onClose]);
  useDialogKeyboard(isOpen, handleEscape, TITLE_ID);

  if (!isOpen) return null;

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={onClose}
      useBodyPortal
      title={title}
      closeAriaLabel={t('memberDetail.confirm.close')}
      titleId={TITLE_ID}
      titlePrefix={
        icon ? (
          <IconHighlight icon={icon} bgColor={iconTone} iconColor={iconTone} size="medium" />
        ) : undefined
      }
      panelClassName={icon ? CENTERED_PANEL : ''}
      closeDisabled={submitting}
      maxWidthClassName={icon ? WIDE_MODAL : 'max-w-md'}
    >
      <div className="flex flex-col">
        <p
          className={
            icon
              ? 'px-6 pb-2 text-center text-sm text-content-secondary'
              : 'px-6 py-4 text-sm text-content-secondary'
          }
        >
          {message}
        </p>
        <div
          className={
            icon
              ? 'flex justify-center gap-2 px-6 pb-6 pt-2'
              : 'flex justify-end gap-2 border-t border-edge px-6 py-4'
          }
        >
          <Button
            variant="secondary"
            size="md"
            data-autofocus
            onClick={onClose}
            disabled={submitting}
          >
            {t('memberDetail.confirm.cancel')}
          </Button>
          <Button variant={confirmVariant} size="md" onClick={handleConfirm} loading={submitting}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}

export default ConfirmActionModal;
